import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatRupiah, formatDate, formatDateTime } from "@/lib/format";
import { PiggyBank, Loader2, CheckCircle2, Landmark, Download, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RupiahInput } from "@/components/RupiahInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { CompanyPicker, SELECT_CLS, today, useCompanies, accLabel } from "@/components/SalesShared";
import { toast } from "sonner";
import { AdminActions, AdminDeleteButton } from "@/components/AdminActions";
import { useBulk, BulkCheck, BulkCheckAll, BulkBar } from "@/components/BulkDelete";
import { useAutoRefresh } from "@/lib/autoRefresh";
import { CashTransferDialog, CashTransferList } from "@/components/FundSource";
import { DepositSummaryCards } from "@/components/DepositSummary";

const DEP_FIELDS = [{ k: "date", l: "Tanggal", t: "date" }, { k: "amount", l: "Jumlah Setor", t: "money" }, { k: "description", l: "Keterangan" },
  { k: "confirmed_amount", l: "Jumlah Diterima (jika sudah dikonfirmasi)", t: "money" }, { k: "confirm_note", l: "Catatan Konfirmasi" }];

const METHOD = { tunai: "Tunai", transfer: "Transfer" };

function BalanceInfo({ bal }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4" data-testid="deposit-balance">
      <div className="rounded-lg bg-secondary p-3"><p className="text-xs text-muted-foreground">Penjualan Tunai</p><p className="font-mono font-bold" data-testid="balance-cash-sales">{formatRupiah(bal.cash_sales || 0)}</p></div>
      <div className="rounded-lg bg-secondary p-3"><p className="text-xs text-muted-foreground">Pembayaran Piutang Tunai</p><p className="font-mono font-bold" data-testid="balance-cash-piutang">{formatRupiah(bal.cash_piutang || 0)}</p></div>
      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3"><p className="text-xs text-emerald-800 font-semibold">Tunai Pusat</p><p className="font-mono font-bold text-emerald-700" data-testid="balance-pusat">{formatRupiah(bal.cash_pusat || 0)}</p><p className="text-[10px] text-muted-foreground">Sudah diterima Pusat{bal.deposit_transfer ? ` · + disetor via transfer ${formatRupiah(bal.deposit_transfer)}` : ""}</p></div>
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3"><p className="text-xs text-amber-800 font-semibold">Tunai Pemohon</p><p className="font-mono font-bold text-amber-700" data-testid="balance-pemohon">{formatRupiah(bal.cash_pemohon || 0)}</p><p className="text-[10px] text-muted-foreground">Termasuk menunggu konfirmasi {formatRupiah(bal.pending || 0)}</p></div>
      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 col-span-2 lg:col-span-1"><p className="text-xs text-emerald-800 font-semibold">Sisa Kas (Bisa Disetor)</p><p className="font-mono font-bold text-brand text-lg" data-testid="balance-remaining">{formatRupiah(bal.balance || 0)}</p></div>
    </div>
  );
}

function DepositForm({ companies, onSaved }) {
  const { user } = useAuth();
  const empty = { date: today(), amount: "", method: "tunai", company_id: user?.company_id || "", account_number: "", description: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [bal, setBal] = useState({ balance: 0 });
  const loadBal = useCallback(() => api.get("/cash-deposits/balance").then((r) => {
    setBal(r.data); setF((p) => ({ ...p, amount: r.data.balance > 0 ? String(r.data.balance) : "" }));
  }).catch(() => {}), []);
  useEffect(() => { loadBal(); }, [loadBal]);
  const over = (Number(f.amount) || 0) > (bal.balance || 0);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setAcc = useCallback((v) => setF((p) => ({ ...p, account_number: v })), []);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.company_id) { toast.error("Pilih PT tujuan"); return; }
    if (f.method === "transfer" && !f.account_number) { toast.error("Pilih PT dan nomor rekening tujuan"); return; }
    if (over) { toast.error(`Jumlah setor melebihi Sisa Kas Tunai (${formatRupiah(bal.balance || 0)})`); return; }
    setBusy(true);
    try {
      await api.post("/cash-deposits", { ...f, amount: Number(f.amount) || 0 });
      toast.success(f.method === "transfer" ? "Setoran transfer dicatat dan langsung diterima." : "Setoran tunai dicatat, menunggu konfirmasi Tim Keuangan.");
      setF(empty); onSaved(); loadBal();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-5">
      <h3 className="font-semibold mb-4" style={{ fontFamily: "Outfit" }}>Form Setor Kas</h3>
      <BalanceInfo bal={bal} />
      <form onSubmit={submit} className="space-y-3" data-testid="deposit-form">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><Label>Tanggal</Label><Input type="date" value={f.date} onChange={set("date")} required className="mt-1.5" data-testid="deposit-date" /></div>
          <div><Label>Jumlah Setor</Label><RupiahInput value={f.amount} onChange={set("amount")} required placeholder="0" className="mt-1.5 font-mono" data-testid="deposit-amount" />
            {over ? <p className="text-[11px] text-rose-600 mt-1" data-testid="deposit-amount-over">Maksimal {formatRupiah(bal.balance || 0)}</p>
              : <p className="text-[11px] text-muted-foreground mt-1" data-testid="deposit-amount-rest">Sisa kas setelah setor: {formatRupiah(Math.max((bal.balance || 0) - (Number(f.amount) || 0), 0))}</p>}</div>
          <div>
            <Label>Metode Setor</Label>
            <select value={f.method} onChange={set("method")} className={`${SELECT_CLS} mt-1.5`} data-testid="deposit-method">
              <option value="tunai">Tunai</option><option value="transfer">Transfer</option>
            </select>
          </div>
        </div>
        <CompanyPicker companies={companies} value={f.company_id} onChange={(v) => setF((p) => ({ ...p, company_id: v }))} account={f.account_number} onAccountChange={setAcc} noAccount={f.method !== "transfer"} testId="deposit-company" />
        <div><Label>Keterangan</Label><Textarea value={f.description} onChange={set("description")} rows={2} className="mt-1.5" data-testid="deposit-desc" /></div>
        <div className="flex justify-end">
          <Button type="submit" disabled={busy || over || !(bal.balance > 0)} className="bg-brand hover:bg-emerald-800" data-testid="deposit-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Setoran"}</Button>
        </div>
      </form>
    </Card>
  );
}

function ConfirmDialog({ d, onDone }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(String(d.amount));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const diff = (Number(amount) || 0) - d.amount;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/cash-deposits/${d.id}/confirm`, { amount: Number(amount) || 0, note });
      toast.success("Setoran dikonfirmasi."); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-brand hover:bg-emerald-800 h-8" data-testid={`deposit-confirm-btn-${d.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Konfirmasi Diterima</Button>
      </DialogTrigger>
      <DialogContent data-testid="deposit-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Konfirmasi Setoran</DialogTitle>
          <DialogDescription>{d.owner_name} · {formatDate(d.date)} · dilaporkan {formatRupiah(d.amount)}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div><Label>Jumlah Diterima (revisi bila ada selisih)</Label><RupiahInput value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 font-mono" data-testid="deposit-confirm-amount" /></div>
          {diff !== 0 && <p className="text-xs text-amber-700" data-testid="deposit-confirm-diff">Selisih {formatRupiah(diff)} — wajib isi catatan.</p>}
          <div><Label>Catatan</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1.5" data-testid="deposit-confirm-note" /></div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="deposit-confirm-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Konfirmasi"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StatusCell({ d }) {
  if (d.status !== "confirmed") return <span className="inline-flex px-2 py-0.5 rounded-full border text-[11px] font-semibold bg-amber-100 text-amber-800 border-amber-200">Menunggu Konfirmasi</span>;
  return (
    <div>
      <span className="inline-flex px-2 py-0.5 rounded-full border text-[11px] font-semibold bg-emerald-100 text-emerald-800 border-emerald-200">Diterima</span>
      <p className="text-[11px] text-muted-foreground mt-0.5">{d.confirmed_by_name} · {formatDateTime(d.confirmed_at)}</p>
      {d.difference ? <p className="text-[11px] text-rose-600 font-mono">Diterima {formatRupiah(d.confirmed_amount)} (selisih {formatRupiah(d.difference)})</p> : null}
      {d.confirm_note && <p className="text-[11px] text-muted-foreground italic">{d.confirm_note}</p>}
    </div>
  );
}

function MutationDialog({ g, onClose }) {
  const [range, setRange] = useState({ start_date: "", end_date: "" });
  const [m, setM] = useState(null);
  const path = g ? `/accounts/${g.company_id}/${encodeURIComponent(g.account_number)}/mutations` : "";
  const params = Object.fromEntries(Object.entries(range).filter(([, v]) => v));
  useEffect(() => {
    if (!g) { setM(null); return; }
    api.get(path, { params }).then((r) => setM(r.data)).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, range.start_date, range.end_date]);
  const exportXlsx = async () => {
    try {
      const r = await api.get(`${path}/export`, { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `mutasi_${g.account_number}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh file"); }
  };
  return (
    <Dialog open={!!g} onOpenChange={(o) => { if (!o) { onClose(); setRange({ start_date: "", end_date: "" }); } }}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="mutation-dialog">
        {g && (
          <>
            <DialogHeader>
              <DialogTitle>Mutasi Rekening — {m?.account?.company_name || g.company_name}</DialogTitle>
              <DialogDescription className="font-mono">{g.bank_name || "Bank"} {g.account_number}{g.account_holder ? ` · a.n. ${g.account_holder}` : ""}</DialogDescription>
            </DialogHeader>
            <div className="flex flex-wrap items-end gap-2">
              <div><Label className="text-xs">Dari</Label><Input type="date" value={range.start_date} onChange={(e) => setRange((r) => ({ ...r, start_date: e.target.value }))} className="h-9 w-40" data-testid="mutation-start-date" /></div>
              <div><Label className="text-xs">Sampai</Label><Input type="date" value={range.end_date} onChange={(e) => setRange((r) => ({ ...r, end_date: e.target.value }))} className="h-9 w-40" data-testid="mutation-end-date" /></div>
              <Button variant="outline" size="sm" className="h-9 ml-auto" onClick={exportXlsx} data-testid="mutation-export-btn"><Download className="h-4 w-4 mr-1" /> Export Excel</Button>
            </div>
            {!m ? <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div> : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  {[["Saldo Awal", m.opening_balance, "text-slate-700", "opening"], ["Total Masuk", m.total_in, "text-emerald-700", "in"], ["Total Keluar", m.total_out, "text-rose-700", "out"], ["Saldo Akhir", m.closing_balance, m.closing_balance < 0 ? "text-rose-700" : "text-brand", "closing"]].map(([l, v, c, k]) => (
                    <div key={k} className="rounded-lg border p-2.5"><p className="text-muted-foreground">{l}</p><p className={`font-mono font-bold ${c}`} data-testid={`mutation-${k}`}>{formatRupiah(v)}</p></div>
                  ))}
                </div>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-xs" data-testid="mutation-table">
                    <thead className="bg-secondary text-muted-foreground">
                      <tr><th className="text-left px-3 py-2">Tanggal</th><th className="text-left px-3 py-2">Keterangan</th><th className="text-right px-3 py-2">Masuk</th><th className="text-right px-3 py-2">Keluar</th><th className="text-right px-3 py-2">Saldo</th></tr>
                    </thead>
                    <tbody>
                      {m.rows.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground" data-testid="mutation-empty">Tidak ada mutasi pada periode ini</td></tr>}
                      {m.rows.map((x, i) => (
                        <tr key={i} className="border-t align-top" data-testid={`mutation-row-${i}`}>
                          <td className="px-3 py-2 whitespace-nowrap">{formatDate(x.date)}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center gap-1 font-medium ${x.type === "in" ? "text-emerald-700" : x.type === "out" ? "text-rose-700" : "text-slate-700"}`}>{x.type === "in" ? <ArrowDownLeft className="h-3 w-3" /> : x.type === "out" ? <ArrowUpRight className="h-3 w-3" /> : <Landmark className="h-3 w-3" />}{x.source}</span>
                            <span className="block text-[11px] text-muted-foreground">{[x.ref, x.party].filter(Boolean).join(" · ")}</span>
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-emerald-700">{x.type === "in" ? formatRupiah(x.amount) : ""}</td>
                          <td className="px-3 py-2 text-right font-mono text-rose-700">{x.type === "out" ? formatRupiah(x.amount) : ""}</td>
                          <td className={`px-3 py-2 text-right font-mono font-semibold ${x.balance < 0 ? "text-rose-700" : ""}`}>{formatRupiah(x.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AccountRecap({ groups, pooled }) {
  const [detail, setDetail] = useState(null);
  const [mut, setMut] = useState(null);
  if (!groups?.length) return null;
  const byPt = groups.reduce((m, g) => { (m[g.company_name] = m[g.company_name] || []).push(g); return m; }, {});
  return (
    <Card className="p-4" data-testid="deposit-account-recap">
      <h3 className="font-semibold mb-1 flex items-center gap-2" style={{ fontFamily: "Outfit" }}><Landmark className="h-4 w-4 text-brand" /> Rekap Dana per PT & Rekening</h3>
      <p className="text-[11px] text-muted-foreground mb-3">Kas Tunai Pusat dihitung gabungan semua PT; baris Tunai per PT menampilkan tunai masuk (periode filter) sebagai catatan sumber. Total PT = Tunai masuk + Tunai Pemohon + dana masuk rekening. Saldo Bersih rekening = Saldo Awal + dana masuk sejak tanggal saldo awal − pembayaran (tidak mengikuti filter).</p>
      {pooled && (
        <button type="button" onClick={() => setMut({ ...pooled, company_id: "ALL", account_number: "TUNAI", company_name: "Semua PT", bank_name: "Kas Tunai Pusat (Gabungan)", account_holder: "" })}
          className="w-full mb-3 text-left flex items-center justify-between gap-3 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 hover:bg-emerald-100 transition-colors" data-testid="recap-pooled-cash">
          <div><p className="text-sm font-semibold text-emerald-900">Kas Tunai Pusat (Gabungan Semua PT)</p>
            <p className="text-[11px] text-muted-foreground">Masuk {formatRupiah(pooled.incoming)} · Keluar {formatRupiah(pooled.outgoing)}{pooled.opening_balance ? ` · Saldo awal ${formatRupiah(pooled.opening_balance)}` : ""} · klik untuk mutasi</p></div>
          <p className={`font-mono text-lg font-bold ${pooled.balance < 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid="recap-pooled-cash-balance">{formatRupiah(pooled.balance)}</p>
        </button>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {Object.entries(byPt).map(([pt, accs]) => (
          <div key={pt} className="rounded-lg border" data-testid={`recap-pt-${pt}`}>
            <div className="flex items-center justify-between px-3 py-2 bg-secondary rounded-t-lg">
              <span className="font-semibold text-sm">{pt || "-"}</span>
              <span className="font-mono text-sm font-bold text-brand" title="Total dana PT: Tunai Pusat + Tunai Pemohon + dana masuk rekening" data-testid={`recap-pt-total-${pt}`}>{formatRupiah(accs.reduce((t, a) => t + (a.is_cash ? (a.tunai_pusat || 0) + (a.tunai_pemohon || 0) : a.total), 0))}</span>
            </div>
            {accs.map((a) => (
              <button type="button" key={a.key} onClick={() => (!a.is_cash && a.net_balance != null ? setMut(a) : setDetail(a))} className="w-full text-left flex items-center justify-between gap-3 px-3 py-2 border-t text-xs hover:bg-secondary/60 transition-colors" data-testid={`recap-acc-${a.is_cash ? `tunai-${pt}` : a.account_number}`}>
                <div className="min-w-0">
                  {a.is_cash ? (
                    <p className="font-semibold text-amber-700">Tunai</p>
                  ) : (
                    <p className="font-mono text-brand truncate"><span className="font-semibold">{a.bank_name || "Bank"}</span> · {a.account_number || "-"}</p>
                  )}
                  <p className="text-muted-foreground">{a.account_holder ? `a.n. ${a.account_holder} · ` : ""}{a.count} transaksi{a.pending ? ` · ${a.pending} belum dikonfirmasi` : ""}</p>
                  {a.is_cash && (
                    <p className="text-[11px] text-slate-600 mt-0.5" data-testid={`recap-tunai-note-${pt}`}>
                      Kas tunai masuk {formatRupiah(a.at_pemohon || 0)}{a.deposit_transfer ? ` · disetor via transfer ${formatRupiah(a.deposit_transfer)} (masuk rekening di bawah)` : ""}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  {a.is_cash ? (
                    <>
                      <p className="text-[11px] text-emerald-700">{a.tunai_in_period != null ? "Tunai masuk" : "Tunai Pusat"} <span className="font-mono font-semibold" data-testid={`recap-tunai-pusat-${pt}`}>{formatRupiah(a.tunai_pusat || 0)}</span></p>
                      {a.opening_date && <p className="text-[11px] text-slate-600 font-mono" data-testid={`recap-tunai-opening-${pt}`}>Saldo Awal {formatRupiah(a.opening_balance || 0)} ({formatDate(a.opening_date)})</p>}
                      <p className="text-[11px] text-amber-700">Tunai Pemohon <span className="font-mono font-semibold" data-testid={`recap-tunai-pemohon-${pt}`}>{formatRupiah(a.tunai_pemohon || 0)}</span></p>
                    </>
                  ) : (
                    <>
                      <p className="font-mono font-semibold text-sm" data-testid={`recap-acc-total-${a.account_number}`}>{formatRupiah(a.total)}</p>
                      <p className="text-[11px] text-emerald-700 font-mono">Diterima {formatRupiah(a.confirmed_total)}</p>
                      {a.net_balance != null && (
                        <>
                          <p className="text-[11px] text-rose-700 font-mono" data-testid={`recap-acc-out-${a.account_number}`}>Keluar {formatRupiah(a.all_outgoing)}</p>
                          {a.opening_date && <p className="text-[11px] text-slate-600 font-mono" data-testid={`recap-acc-opening-${a.account_number}`}>Saldo Awal {formatRupiah(a.opening_balance)} ({formatDate(a.opening_date)})</p>}
                          <p className={`text-[11px] font-mono font-bold ${a.net_balance < 0 ? "text-rose-700" : "text-brand"}`} data-testid={`recap-acc-net-${a.account_number}`}>Saldo Bersih {formatRupiah(a.net_balance)}</p>
                          <p className="text-[10px] text-muted-foreground">klik untuk mutasi</p>
                        </>
                      )}
                    </>
                  )}
                </div>
              </button>
            ))}
          </div>
        ))}
      </div>
      <RecapDetailDialog g={detail} onClose={() => setDetail(null)} onMutasi={(g) => { setDetail(null); setMut({ ...g, account_number: "TUNAI", bank_name: "Tunai Pusat", account_holder: "" }); }} />
      <MutationDialog g={mut} onClose={() => setMut(null)} />
    </Card>
  );
}

const ENTRY_STATUS_CLS = {
  "Diterima": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Masuk Rekening": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Menunggu Konfirmasi": "bg-amber-100 text-amber-800 border-amber-200",
  "Tunai di Pemohon": "bg-slate-100 text-slate-700 border-slate-200",
};

function RecapDetailDialog({ g, onClose, onMutasi }) {
  return (
    <Dialog open={!!g} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="recap-detail-dialog">
        {g && (
          <>
            <DialogHeader>
              <DialogTitle>{g.company_name} — {g.is_cash ? "Tunai" : `${g.bank_name || "Bank"} ${g.account_number}`}</DialogTitle>
              <DialogDescription>{g.count} transaksi · Total {formatRupiah(g.total)} · Diterima {formatRupiah(g.confirmed_total)}</DialogDescription>
            </DialogHeader>
            {g.is_cash && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="recap-tunai-info">
                Dana berstatus <b>Tunai di Pemohon</b> masih dipegang Pemohon. <b>Tunai Pemohon</b> saat ini {formatRupiah(g.tunai_pemohon || 0)}, <b>Tunai masuk</b> PT ini (periode filter, sudah dikonfirmasi) {formatRupiah(g.tunai_pusat || 0)} — saldo Kas Tunai Pusat dihitung gabungan semua PT.
                {g.tunai_all_in != null && g.company_id && <Button size="sm" variant="outline" className="mt-2 h-7 block" onClick={() => onMutasi(g)} data-testid="recap-tunai-mutasi-btn">Lihat Mutasi Tunai Pusat</Button>}
              </div>
            )}
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-xs" data-testid="recap-detail-table">
                <thead className="bg-secondary text-muted-foreground">
                  <tr><th className="text-left px-3 py-2">Tanggal</th><th className="text-left px-3 py-2">Nama Pemohon</th><th className="text-left px-3 py-2">Sumber Dana</th>
                    <th className="text-right px-3 py-2">Nominal</th><th className="text-left px-3 py-2">Status</th></tr>
                </thead>
                <tbody>
                  {g.entries.map((x, i) => (
                    <tr key={i} className="border-t align-top" data-testid={`recap-entry-${i}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{formatDate(x.date)}</td>
                      <td className="px-3 py-2">{x.owner_name}</td>
                      <td className="px-3 py-2">{x.source}{x.ref && <span className="block text-[11px] text-muted-foreground">{x.ref}</span>}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatRupiah(x.amount)}{x.received != null && x.received !== x.amount && <span className="block text-[11px] text-rose-600">diterima {formatRupiah(x.received)}</span>}</td>
                      <td className="px-3 py-2"><span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-semibold ${ENTRY_STATUS_CLS[x.status] || ""}`}>{x.status}</span>{x.note && <span className="block text-[10px] text-muted-foreground mt-0.5 max-w-[180px]">{x.note}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function CashDeposits() {
  const { user } = useAuth();
  const isPemohon = user?.role === "pemohon";
  const canConfirm = user?.role === "tim_keuangan";
  const isAdmin = user?.role === "it_admin";
  const bulk = useBulk();
  const companies = useCompanies();
  const [branches, setBranches] = useState([]);
  const [fl, setFl] = useState({ start_date: "", end_date: "", method: "", branch_id: "", status: "", company_id: "" });
  const [data, setData] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const canTransfer = ["tim_keuangan", "it_admin"].includes(user?.role);
  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(fl).filter(([, v]) => v));
    api.get("/cash-deposits", { params }).then((r) => setData(r.data)).catch(() => setData({ rows: [], summary: {} }));
    if (!isPemohon) api.get("/cash-transfers", { params: fl.company_id && fl.company_id !== "none" ? { company_id: fl.company_id } : {} }).then((r) => setTransfers(r.data)).catch(() => {});
  }, [fl, isPemohon]);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);
  useEffect(() => { if (!isPemohon) api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, [isPemohon]);
  const setF = (k) => (e) => setFl((p) => ({ ...p, [k]: e.target.value }));
  const s = data?.summary || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}><PiggyBank className="h-6 w-6 text-brand" /> Setor Kas</h2>
          <p className="text-sm text-muted-foreground">{isPemohon ? "Catat setoran kas Anda. Tim Keuangan akan mengonfirmasi penerimaan." : "Riwayat setoran kas dari Pemohon."}</p>
        </div>
        {canTransfer && <CashTransferDialog onDone={load} />}
      </div>
      {isPemohon && <DepositForm companies={companies} onSaved={load} />}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3" data-testid="deposit-filters">
          <div><Label className="text-xs">Dari</Label><Input type="date" value={fl.start_date} onChange={setF("start_date")} className="mt-1 h-9" data-testid="deposit-filter-start" /></div>
          <div><Label className="text-xs">Sampai</Label><Input type="date" value={fl.end_date} onChange={setF("end_date")} className="mt-1 h-9" data-testid="deposit-filter-end" /></div>
          <div><Label className="text-xs block">Metode</Label>
            <select value={fl.method} onChange={setF("method")} className={`${SELECT_CLS} mt-1 h-9 w-36`} data-testid="deposit-filter-method">
              <option value="">Semua</option><option value="tunai">Tunai</option><option value="transfer">Transfer</option>
            </select></div>
          <div><Label className="text-xs">Status</Label>
            <select value={fl.status} onChange={setF("status")} className={`${SELECT_CLS} mt-1 h-9 w-44`} data-testid="deposit-filter-status">
              <option value="">Semua</option><option value="pending">Menunggu Konfirmasi</option><option value="confirmed">Diterima</option>
            </select></div>
          {!isPemohon && (
            <div><Label className="text-xs">Cabang</Label>
              <select value={fl.branch_id} onChange={setF("branch_id")} className={`${SELECT_CLS} mt-1 h-9 w-44`} data-testid="deposit-filter-branch">
                <option value="">Semua Cabang</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select></div>
          )}
          <div><Label className="text-xs">PT</Label>
            <select value={fl.company_id} onChange={setF("company_id")} className={`${SELECT_CLS} mt-1 h-9 w-48`} data-testid="deposit-filter-pt">
              <option value="">Semua PT</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}<option value="none">Tanpa PT</option>
            </select></div>
        </div>
        <div className="mt-4"><DepositSummaryCards s={s} held={data?.pemohon_held || []} isPemohon={isPemohon} /></div>
      </Card>
      {!isPemohon && <AccountRecap groups={data?.by_account} pooled={data?.pooled_cash} />}
      {!isPemohon && <CashTransferList rows={transfers} isAdmin={isAdmin} onDone={load} />}
      <BulkBar bulk={bulk} onDone={load} label="setoran" />
      <Card className="p-0 overflow-hidden">
        {!data ? <div className="p-6"><div className="h-4 rounded-md skeleton-shimmer" /></div> : data.rows.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground" data-testid="deposit-empty">Belum ada setoran.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="deposit-table">
              <thead className="bg-secondary text-xs uppercase text-muted-foreground">
                <tr>{isAdmin && <th className="pl-4 py-2.5 w-8"><BulkCheckAll bulk={bulk} entries={data.rows.map((d) => [d.id, { kind: "cash-deposits", id: d.id }])} testId="bulk-deposit-all" /></th>}<th className="text-left px-4 py-2.5">Tanggal</th>{!isPemohon && <th className="text-left px-4 py-2.5">Pemohon</th>}
                  <th className="text-left px-4 py-2.5">Metode</th><th className="text-left px-4 py-2.5">Keterangan</th>
                  <th className="text-right px-4 py-2.5">Jumlah</th><th className="text-left px-4 py-2.5">Status</th>{(canConfirm || isAdmin) && <th className="px-4 py-2.5"></th>}</tr>
              </thead>
              <tbody>
                {data.rows.map((d) => (
                  <tr key={d.id} className="border-t align-top" data-testid={`deposit-row-${d.id}`}>
                    {isAdmin && <td className="pl-4 py-2.5"><BulkCheck bulk={bulk} k={d.id} item={{ kind: "cash-deposits", id: d.id }} testId={`bulk-deposit-${d.id}`} /></td>}
                    <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(d.date)}</td>
                    {!isPemohon && <td className="px-4 py-2.5">{d.owner_name}<span className="block text-xs text-muted-foreground">{d.branch_name}</span></td>}
                    <td className="px-4 py-2.5">{METHOD[d.method]}{d.company_name && <span className="block text-xs text-muted-foreground">{d.company_name}{d.target_account && <span className="block font-mono" data-testid={`deposit-account-${d.id}`}>{accLabel(d.target_account)}</span>}</span>}</td>
                    <td className="px-4 py-2.5 text-xs">{d.description || "-"}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{formatRupiah(d.amount)}</td>
                    <td className="px-4 py-2.5"><StatusCell d={d} /></td>
                    {canConfirm && <td className="px-4 py-2.5 text-right">{d.status !== "confirmed" && <ConfirmDialog d={d} onDone={load} />}</td>}
                    {isAdmin && (
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        {d.status === "confirmed" && d.method !== "transfer" && <AdminDeleteButton path={`/admin/reset/deposit-confirmation/${d.id}`} onDone={load} label="Hapus Konfirmasi" testId={`admin-reset-deposit-${d.id}`} confirmText="Konfirmasi penerimaan dihapus permanen. Setoran kembali ke status Menunggu Konfirmasi." />}
                        <AdminActions editPath={`/admin/records/cash-deposits/${d.id}`} deletePath={`/admin/records/cash-deposits/${d.id}`} fields={DEP_FIELDS} values={d} onDone={load} title="Edit Setoran" testId={`admin-deposit-${d.id}`} confirmText="Setoran dihapus permanen. Sisa kas tunai pemohon kembali bertambah dan setoran hilang dari semua rekap." />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
